import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Stack,
  TextField,
  Divider,
  Link as MuiLink,
} from '@mui/material';
import { useAuth } from '../auth/AuthContext';

const ADMIN_USERS_ENDPOINT = '/api/admin/users';
const ADMIN_LLM_ENDPOINT = '/api/admin/llm';
const ADMIN_LLM_TOKEN_ENDPOINT = '/api/admin/llm/token';
const ADMIN_CONFLUENCE_ENDPOINT = '/api/admin/confluence';
const DEFAULT_LLM_PROVIDERS = ['openai', 'ollama', 'pflb_llm'];
const PFLB_PRO_PROVIDER = 'pflb_llm_pro';
const DEFAULT_DISPLAY_PROVIDER = PFLB_PRO_PROVIDER;
const TOKEN_SUPPORTED_PROVIDERS = ['openai', 'pflb_llm', PFLB_PRO_PROVIDER];
const GPT_41_MODEL_ID = 'gpt-4.1';
const PROVIDER_STATUS_ONLINE = 'online';
const PROVIDER_STATUS_OFFLINE = 'offline';
const PROVIDER_STATUS_CHECKING = 'checking';
const OLLAMA_MODEL_LABELS = {
  smallthinker: 'Компактная',
  'smallthinker:latest': 'Компактная',
  'qwen3:4b': 'Qwen 3 4B',
  'zahar:latest': 'Захар актуальная',
};

const PROVIDER_STATUS_META = {
  [PROVIDER_STATUS_ONLINE]: {
    label: 'Онлайн',
    color: '#2f7a46',
    borderColor: 'rgba(65, 154, 89, 0.45)',
    backgroundColor: 'rgba(96, 182, 118, 0.15)',
  },
  [PROVIDER_STATUS_OFFLINE]: {
    label: 'Оффлайн',
    color: '#b2463f',
    borderColor: 'rgba(231, 106, 95, 0.45)',
    backgroundColor: 'rgba(236, 125, 114, 0.15)',
  },
  [PROVIDER_STATUS_CHECKING]: {
    label: 'Проверка',
    color: '#626d80',
    borderColor: 'rgba(126, 136, 154, 0.4)',
    backgroundColor: 'rgba(140, 150, 166, 0.12)',
  },
};

const normalizeProviderId = (provider) => {
  if (typeof provider !== 'string') {
    return '';
  }
  return provider.trim().toLowerCase();
};

const normalizeModelId = (model) => {
  if (typeof model !== 'string') {
    return '';
  }
  return model.trim().toLowerCase();
};

const formatProviderLabel = (provider) => {
  if (provider === 'openai') {
    return 'OpenAI';
  }
  if (provider === 'ollama') {
    return 'Ollama';
  }
  if (provider === 'pflb_llm') {
    return 'Lumera LLM';
  }
  if (provider === PFLB_PRO_PROVIDER) {
    return 'Lumera LLM PRO';
  }
  return provider || '—';
};

const providerToApiProvider = (provider) => {
  const normalized = normalizeProviderId(provider);
  if (normalized === PFLB_PRO_PROVIDER) {
    return 'openai';
  }
  return normalized;
};

const buildVisibleProviders = (providers) => {
  const source = Array.from(new Set((Array.isArray(providers) ? providers : DEFAULT_LLM_PROVIDERS)
    .map((item) => normalizeProviderId(item))
    .filter(Boolean)));

  const result = [];
  source.forEach((provider) => {
    if (!result.includes(provider)) {
      result.push(provider);
    }
    if (provider === 'openai' && !result.includes(PFLB_PRO_PROVIDER)) {
      result.push(PFLB_PRO_PROVIDER);
    }
  });

  if (result.includes(PFLB_PRO_PROVIDER)) {
    return [PFLB_PRO_PROVIDER, ...result.filter((provider) => provider !== PFLB_PRO_PROVIDER)];
  }

  return result;
};

const buildVisibleModels = (provider, models) => {
  const normalizedProvider = normalizeProviderId(provider);
  const source = Array.from(new Set((Array.isArray(models) ? models : [])
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean)));

  if (normalizedProvider === 'openai') {
    return source.filter((model) => normalizeModelId(model) !== GPT_41_MODEL_ID);
  }

  if (normalizedProvider === PFLB_PRO_PROVIDER) {
    const hasGpt41 = source.some((model) => normalizeModelId(model) === GPT_41_MODEL_ID);
    if (hasGpt41) {
      return source;
    }
    return [...source, 'gpt-4.1'];
  }

  return source;
};

const pickMatchingModel = (requestedModel, models) => {
  const normalizedRequested = normalizeModelId(requestedModel);
  if (!normalizedRequested || !Array.isArray(models)) {
    return '';
  }
  return models.find((item) => normalizeModelId(item) === normalizedRequested) || '';
};

const formatModelLabel = (provider, model) => {
  if (typeof model !== 'string' || model.trim() === '') {
    return '—';
  }
  if (normalizeProviderId(provider) !== 'ollama') {
    return model;
  }
  const ruLabel = OLLAMA_MODEL_LABELS[normalizeModelId(model)];
  if (!ruLabel) {
    return model;
  }
  return `${ruLabel} (${model})`;
};

const getUserKey = (item) => (item?.id != null ? String(item.id) : item?.username ?? '');

const formatCreditDisplay = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00';
};

const formatCreditInput = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00';
};

const parseCreditValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return Number.NaN;
  }
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const createInitialNewUserFormState = () => ({
  username: '',
  credit: '',
});

const createInitialNewUserValidationState = () => ({
  username: false,
  credit: false,
});

export default function AdminUsersPage() {
  const { apiClient, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creditInputs, setCreditInputs] = useState({});
  const [saving, setSaving] = useState({});
  const [actionStatus, setActionStatus] = useState(null);
  const [newUserForm, setNewUserForm] = useState(() => createInitialNewUserFormState());
  const [newUserValidation, setNewUserValidation] = useState(() => createInitialNewUserValidationState());
  const [creatingUser, setCreatingUser] = useState(false);
  const [copyStatus, setCopyStatus] = useState(null);
  const [llmConfig, setLlmConfig] = useState(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmSaving, setLlmSaving] = useState(false);
  const [tokenSaving, setTokenSaving] = useState(false);
  const [llmError, setLlmError] = useState(null);
  const [llmStatus, setLlmStatus] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(DEFAULT_DISPLAY_PROVIDER);
  const [selectedModel, setSelectedModel] = useState('');
  const [cloudTokenDraft, setCloudTokenDraft] = useState('');
  const [providerStatuses, setProviderStatuses] = useState({});
  const [confluenceConfig, setConfluenceConfig] = useState(null);
  const [confluenceForm, setConfluenceForm] = useState({
    baseUrl: '',
    email: '',
    authType: 'Bearer',
    apiToken: '',
  });
  const [confluenceLoading, setConfluenceLoading] = useState(false);
  const [confluenceSaving, setConfluenceSaving] = useState(false);
  const [confluenceStatus, setConfluenceStatus] = useState(null);

  const isAdmin = useMemo(() => Array.isArray(user?.roles) && user.roles.includes('ADMIN'), [user]);

  const computeInitialPasswordLink = useCallback((relativeLink) => {
    if (!relativeLink) {
      return '';
    }
    if (typeof window === 'undefined') {
      return relativeLink;
    }
    try {
      return new URL(relativeLink, window.location.origin).toString();
    } catch (err) {
      return relativeLink;
    }
  }, []);

  const loadLlmConfig = useCallback(async (provider = '', syncProviderFromResponse = false) => {
    setLlmLoading(true);
    setLlmError(null);
    try {
      const requestedDisplayProvider = normalizeProviderId(provider);
      const requestedApiProvider = providerToApiProvider(provider);
      const requestConfig = { withCredentials: true };
      if (requestedApiProvider) {
        requestConfig.params = { provider: requestedApiProvider };
      }
      const response = await apiClient.get(ADMIN_LLM_ENDPOINT, requestConfig);
      const config = response?.data ?? null;
      setLlmConfig(config);
      const currentProvider = typeof config?.currentProvider === 'string' ? config.currentProvider : '';
      const responseModelsProvider = typeof config?.modelsProvider === 'string' ? config.modelsProvider : '';
      const modelsProvider = requestedDisplayProvider || responseModelsProvider || currentProvider;
      const availableModels = Array.isArray(config?.availableModels) ? config.availableModels : [];
      const availableProviders = (Array.isArray(config?.availableProviders) && config.availableProviders.length > 0)
        ? config.availableProviders
        : DEFAULT_LLM_PROVIDERS;
      const resolvedVisibleProviders = buildVisibleProviders(availableProviders);
      const shouldUseOpenAiAlias = providerToApiProvider(modelsProvider) === 'openai'
        && resolvedVisibleProviders.includes(PFLB_PRO_PROVIDER);
      const displayProvider = shouldUseOpenAiAlias ? PFLB_PRO_PROVIDER : modelsProvider;
      const visibleModels = buildVisibleModels(displayProvider || modelsProvider, availableModels);
      const normalizedModelsProvider = providerToApiProvider(displayProvider || modelsProvider);

      if (currentProvider) {
        setProviderStatuses((prev) => ({
          ...prev,
          [providerToApiProvider(currentProvider)]: PROVIDER_STATUS_ONLINE,
          ...(normalizedModelsProvider ? { [normalizedModelsProvider]: PROVIDER_STATUS_ONLINE } : {}),
        }));
      }

      if (syncProviderFromResponse) {
        setSelectedProvider(displayProvider || resolvedVisibleProviders[0] || DEFAULT_DISPLAY_PROVIDER);
      }

      if (displayProvider && currentProvider
        && providerToApiProvider(displayProvider) === providerToApiProvider(currentProvider)
        && typeof config?.currentModel === 'string') {
        const matchedCurrent = pickMatchingModel(config.currentModel, visibleModels);
        setSelectedModel(matchedCurrent || (visibleModels[0] || ''));
      } else if (visibleModels.length > 0) {
        setSelectedModel(visibleModels[0]);
      } else {
        setSelectedModel('');
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Не удалось загрузить список моделей';
      setLlmError(message);
      const normalizedRequestedProvider = providerToApiProvider(provider);
      if (normalizedRequestedProvider) {
        setProviderStatuses((prev) => ({
          ...prev,
          [normalizedRequestedProvider]: PROVIDER_STATUS_OFFLINE,
        }));
      }
      if (!provider) {
        setSelectedProvider((prev) => prev || DEFAULT_DISPLAY_PROVIDER);
      }
    } finally {
      setLlmLoading(false);
    }
  }, [apiClient]);

  const visibleModels = useMemo(
    () => buildVisibleModels(selectedProvider, llmConfig?.availableModels),
    [selectedProvider, llmConfig?.availableModels]
  );

  const visibleProviders = useMemo(
    () => buildVisibleProviders(llmConfig?.availableProviders),
    [llmConfig?.availableProviders]
  );

  const loadConfluenceConfig = useCallback(async () => {
    setConfluenceLoading(true);
    setConfluenceStatus(null);
    try {
      const response = await apiClient.get(ADMIN_CONFLUENCE_ENDPOINT, { withCredentials: true });
      const config = response?.data ?? {};
      setConfluenceConfig(config);
      setConfluenceForm({
        baseUrl: config?.baseUrl || '',
        email: config?.email || '',
        authType: config?.authType || 'Bearer',
        apiToken: '',
      });
    } catch (err) {
      setConfluenceStatus({
        type: 'error',
        message: err?.response?.data?.message || err?.message || 'Не удалось загрузить настройки Confluence',
      });
    } finally {
      setConfluenceLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(ADMIN_USERS_ENDPOINT, { withCredentials: true });
        if (!cancelled) {
          const data = Array.isArray(response?.data) ? response.data : [];
          setUsers(data);
          setCreditInputs(data.reduce((acc, item) => {
            const key = getUserKey(item);
            if (key) {
              acc[key] = formatCreditInput(item?.creditBalance);
            }
            return acc;
          }, {}));
        }
      } catch (err) {
        if (!cancelled) {
          const message = err?.response?.data?.message || err?.message || 'Не удалось загрузить список пользователей';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (isAdmin) {
      fetchUsers();
      loadLlmConfig('', true);
      loadConfluenceConfig();
    } else {
      setLoading(false);
      setUsers([]);
      setCreditInputs({});
      setActionStatus(null);
      setLlmConfig(null);
      setLlmError(null);
      setLlmStatus(null);
      setSelectedProvider(DEFAULT_DISPLAY_PROVIDER);
      setSelectedModel('');
      setCloudTokenDraft('');
      setProviderStatuses({});
      setConfluenceConfig(null);
      setConfluenceForm({ baseUrl: '', email: '', authType: 'Bearer', apiToken: '' });
      setConfluenceStatus(null);
    }

    return () => {
      cancelled = true;
    };
  }, [apiClient, isAdmin, loadConfluenceConfig, loadLlmConfig]);

  const handleCreditChange = (key, value) => {
    setCreditInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSetCredits = async (item) => {
    if (!item?.id) {
      setActionStatus({
        type: 'error',
        message: 'Для выбранного пользователя отсутствует идентификатор',
        passwordSetup: null,
        passwordSetupRequired: false,
      });
      return;
    }
    const key = getUserKey(item);
    const rawValue = creditInputs[key];
    const parsedValue = parseCreditValue(rawValue);
    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
      setActionStatus({
        type: 'error',
        message: 'Введите неотрицательное число кредитов',
        passwordSetup: null,
        passwordSetupRequired: false,
      });
      return;
    }
    const normalizedValue = Number(parsedValue.toFixed(2));
    setSaving((prev) => ({ ...prev, [key]: true }));
    setActionStatus(null);
    try {
      const response = await apiClient.put(
        `${ADMIN_USERS_ENDPOINT}/${item.id}/credits`,
        { creditBalance: normalizedValue },
        { withCredentials: true }
      );
      const updated = response?.data;
      if (updated?.id != null) {
        setUsers((prev) => prev.map((userItem) => (userItem.id === updated.id ? { ...userItem, ...updated } : userItem)));
        setCreditInputs((prev) => ({ ...prev, [key]: formatCreditInput(updated.creditBalance ?? normalizedValue) }));
        setActionStatus({
          type: 'success',
          message: `Лимит для пользователя «${updated.username ?? item.username}» обновлён`,
          passwordSetup: null,
          passwordSetupRequired: false,
        });
      } else {
        setCreditInputs((prev) => ({ ...prev, [key]: formatCreditInput(normalizedValue) }));
        setActionStatus({
          type: 'success',
          message: `Лимит для пользователя «${item.username}» обновлён`,
          passwordSetup: null,
          passwordSetupRequired: false,
        });
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Не удалось обновить лимит кредитов';
      setActionStatus({ type: 'error', message, passwordSetup: null, passwordSetupRequired: false });
    } finally {
      setSaving((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleNewUserFieldChange = (field) => (event) => {
    const value = event.target.value;
    setNewUserForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateNewUserForm = () => {
    const trimmedUsername = newUserForm.username.trim();
    const parsedCredit = parseCreditValue(newUserForm.credit);
    const hasCreditInput = newUserForm.credit !== '' && newUserForm.credit !== null && newUserForm.credit !== undefined;
    const hasCreditError = hasCreditInput && (!Number.isFinite(parsedCredit) || parsedCredit < 0);

    const validationResult = {
      username: trimmedUsername === '',
      credit: hasCreditError,
    };

    setNewUserValidation(validationResult);

    if (Object.values(validationResult).some(Boolean)) {
      setActionStatus({
        type: 'error',
        message: 'Проверьте введённые данные и повторите попытку',
        passwordSetup: null,
        passwordSetupRequired: false,
      });
      return null;
    }

    return {
      trimmedUsername,
      parsedCredit,
      hasCreditInput,
    };
  };

  const handleCreateNewUser = async (event) => {
    event.preventDefault();
    if (creatingUser) {
      return;
    }

    const validation = validateNewUserForm();
    if (!validation) {
      return;
    }

    const { trimmedUsername, parsedCredit, hasCreditInput } = validation;

    const payload = {
      username: trimmedUsername,
    };

    if (hasCreditInput && Number.isFinite(parsedCredit)) {
      payload.creditBalance = Number(parsedCredit.toFixed(2));
    }

    setCreatingUser(true);
    setActionStatus(null);
    try {
      const response = await apiClient.post(ADMIN_USERS_ENDPOINT, payload, { withCredentials: true });
      const created = response?.data;
      if (created) {
        setUsers((prev) => [...prev, created]);
        const key = getUserKey(created);
        if (key) {
          setCreditInputs((prev) => ({
            ...prev,
            [key]: formatCreditInput(created.creditBalance),
          }));
        }
        setNewUserForm(createInitialNewUserFormState());
        setNewUserValidation(createInitialNewUserValidationState());
        setActionStatus({
          type: 'success',
          message: `Пользователь «${created.username ?? trimmedUsername}» создан. Передайте токен пользователю.`,
          passwordSetup: created?.passwordSetup ?? null,
          passwordSetupRequired: Boolean(created?.passwordSetupRequired),
        });
      } else {
        setActionStatus({
          type: 'success',
          message: `Пользователь «${trimmedUsername}» создан`,
          passwordSetup: null,
          passwordSetupRequired: false,
        });
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Не удалось создать пользователя';
      setActionStatus({ type: 'error', message, passwordSetup: null, passwordSetupRequired: false });
    } finally {
      setCreatingUser(false);
    }
  };

  const handleProviderChange = async (event) => {
    const nextProvider = event.target.value;
    const normalizedProvider = providerToApiProvider(nextProvider);
    if (normalizedProvider) {
      setProviderStatuses((prev) => ({
        ...prev,
        [normalizedProvider]: PROVIDER_STATUS_CHECKING,
      }));
    }
    setSelectedProvider(nextProvider);
    setSelectedModel('');
    setCloudTokenDraft('');
    await loadLlmConfig(nextProvider, false);
  };

  const getProviderStatusMeta = useCallback((provider) => {
    const normalizedProvider = providerToApiProvider(provider);
    const status = providerStatuses[normalizedProvider] || PROVIDER_STATUS_CHECKING;
    return PROVIDER_STATUS_META[status] || PROVIDER_STATUS_META[PROVIDER_STATUS_CHECKING];
  }, [providerStatuses]);

  const renderProviderStatusBadge = useCallback((provider) => {
    const statusMeta = getProviderStatusMeta(provider);
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 72,
          px: 1.1,
          py: '2px',
          borderRadius: '999px',
          border: '1px solid',
          borderColor: statusMeta.borderColor,
          bgcolor: statusMeta.backgroundColor,
          color: statusMeta.color,
          fontSize: '0.66rem',
          fontWeight: 700,
          lineHeight: 1.2,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {statusMeta.label}
      </Box>
    );
  }, [getProviderStatusMeta]);

  const handleSaveCloudToken = async () => {
    const provider = normalizeProviderId(selectedProvider);
    const apiProvider = providerToApiProvider(selectedProvider);
    const token = typeof cloudTokenDraft === 'string' ? cloudTokenDraft.trim() : '';
    const providerLabel = formatProviderLabel(provider);
    if (!TOKEN_SUPPORTED_PROVIDERS.includes(provider)) {
      setLlmStatus({ type: 'error', message: 'Сохранение токена доступно только для OpenAI, Lumera LLM и Lumera LLM PRO' });
      return;
    }
    if (!token) {
      setLlmStatus({ type: 'error', message: `Введите токен для ${providerLabel}` });
      return;
    }

    setTokenSaving(true);
    setLlmStatus(null);
    try {
      const response = await apiClient.put(
        ADMIN_LLM_TOKEN_ENDPOINT,
        { provider: apiProvider, token },
        { withCredentials: true }
      );
      const updated = response?.data ?? null;
      setLlmConfig(updated);
      setCloudTokenDraft('');
      setLlmError(null);
      const modelsForProvider = buildVisibleModels(selectedProvider, updated?.availableModels);
      if (modelsForProvider.length > 0) {
        setSelectedModel((prev) => {
          const matched = pickMatchingModel(prev, modelsForProvider);
          return matched || modelsForProvider[0];
        });
      } else {
        setSelectedModel('');
      }
      setLlmStatus({
        type: 'success',
        message: `Токен ${providerLabel} сохранён`,
      });
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || `Не удалось сохранить токен ${providerLabel}`;
      setLlmStatus({ type: 'error', message });
    } finally {
      setTokenSaving(false);
    }
  };

  const handleApplyLlmModel = async () => {
    const provider = normalizeProviderId(selectedProvider);
    const apiProvider = providerToApiProvider(selectedProvider);
    const model = typeof selectedModel === 'string' ? selectedModel.trim() : '';
    if (!provider) {
      setLlmStatus({ type: 'error', message: 'Выберите источник моделей' });
      return;
    }
    if (!model) {
      setLlmStatus({ type: 'error', message: 'Выберите модель для применения' });
      return;
    }

    setLlmSaving(true);
    setLlmStatus(null);
    try {
      const response = await apiClient.put(
        ADMIN_LLM_ENDPOINT,
        { provider: apiProvider, model },
        { withCredentials: true }
      );
      const updated = response?.data ?? null;
      setLlmConfig(updated);
      const updatedProvider = typeof updated?.currentProvider === 'string' ? updated.currentProvider : apiProvider;
      const selectedDisplayProvider = provider === PFLB_PRO_PROVIDER
        && providerToApiProvider(updatedProvider) === apiProvider
        ? PFLB_PRO_PROVIDER
        : updatedProvider;
      const updatedModel = typeof updated?.currentModel === 'string' ? updated.currentModel : model;
      const modelsForProvider = buildVisibleModels(selectedDisplayProvider, updated?.availableModels);
      const selectedModelValue = pickMatchingModel(updatedModel, modelsForProvider) || modelsForProvider[0] || '';
      setSelectedProvider(selectedDisplayProvider);
      setSelectedModel(selectedModelValue);
      setLlmError(null);
      setLlmStatus({
        type: 'success',
        message: `Источник: ${formatProviderLabel(selectedDisplayProvider)}, модель: «${formatModelLabel(selectedDisplayProvider, updatedModel)}»`,
      });
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Не удалось переключить LLM-конфигурацию';
      setLlmStatus({ type: 'error', message });
    } finally {
      setLlmSaving(false);
    }
  };

  const handleConfluenceFieldChange = (field) => (event) => {
    setConfluenceForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSaveConfluenceConfig = async () => {
    const baseUrl = confluenceForm.baseUrl.trim();
    const authType = confluenceForm.authType.trim() || 'Bearer';
    if (!baseUrl) {
      setConfluenceStatus({ type: 'error', message: 'Укажите адрес Confluence' });
      return;
    }
    if (!confluenceForm.apiToken.trim() && !confluenceConfig?.apiTokenConfigured) {
      setConfluenceStatus({ type: 'error', message: 'Укажите API token' });
      return;
    }

    setConfluenceSaving(true);
    setConfluenceStatus(null);
    try {
      const response = await apiClient.put(ADMIN_CONFLUENCE_ENDPOINT, {
        baseUrl,
        email: confluenceForm.email.trim(),
        authType,
        apiToken: confluenceForm.apiToken.trim(),
      }, { withCredentials: true });
      const updated = response?.data ?? {};
      setConfluenceConfig(updated);
      setConfluenceForm({
        baseUrl: updated?.baseUrl || '',
        email: updated?.email || '',
        authType: updated?.authType || 'Bearer',
        apiToken: '',
      });
      setConfluenceStatus({
        type: 'success',
        message: updated?.connected ? 'Confluence подключен' : 'Настройки Confluence сохранены',
      });
    } catch (err) {
      setConfluenceStatus({
        type: 'error',
        message: err?.response?.data?.message || err?.message || 'Не удалось сохранить настройки Confluence',
      });
    } finally {
      setConfluenceSaving(false);
    }
  };

  const handleCopyToClipboard = useCallback(async (value, type) => {
    if (!value) {
      setCopyStatus({ type: 'error', message: 'Нет данных для копирования' });
      return;
    }

    try {
      const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;
      if (clipboard?.writeText) {
        await clipboard.writeText(value);
      } else if (typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      } else {
        throw new Error('Clipboard is unavailable');
      }
      setCopyStatus({
        type: 'success',
        message: type === 'link' ? 'Ссылка скопирована в буфер обмена' : 'Токен скопирован в буфер обмена',
      });
    } catch (err) {
      setCopyStatus({ type: 'error', message: 'Не удалось скопировать автоматически. Скопируйте вручную.' });
    }
  }, []);

  useEffect(() => {
    if (!copyStatus) {
      return undefined;
    }
    const timeout = setTimeout(() => setCopyStatus(null), 4000);
    return () => clearTimeout(timeout);
  }, [copyStatus]);

  useEffect(() => {
    if (!actionStatus) {
      setCopyStatus(null);
    }
  }, [actionStatus]);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Доступ к сводке пользователей доступен только администраторам.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: '960px', margin: '0 auto' }} className="stagger-children">
      <Typography variant="h5" gutterBottom>
        Пользователи и кредиты
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Здесь отображаются текущие остатки и суммарные траты кредитов всех пользователей системы.
      </Typography>

      {actionStatus && (
        <Alert severity={actionStatus.type} sx={{ mb: 2 }} onClose={() => setActionStatus(null)}>
          {(() => {
            const passwordSetup = actionStatus.passwordSetup;
            const resolvedLink = passwordSetup?.link ? computeInitialPasswordLink(passwordSetup.link) : null;
            const showPasswordNotice =
              actionStatus.passwordSetupRequired || Boolean(passwordSetup?.token) || Boolean(resolvedLink);

            return (
              <Stack spacing={1.5}>
                <Typography variant="body2">{actionStatus.message}</Typography>
                {showPasswordNotice && (
                  <Stack spacing={1.25}>
                    {actionStatus.passwordSetupRequired && (
                      <Typography variant="body2" color="text.secondary">
                        Пользователь должен установить пароль при первом входе.
                      </Typography>
                    )}
                    {passwordSetup?.token && (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ sm: 'center' }}
                        useFlexGap
                      >
                        <TextField
                          label="Токен установки пароля"
                          value={passwordSetup.token}
                          InputProps={{ readOnly: true }}
                          size="small"
                          fullWidth
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleCopyToClipboard(passwordSetup.token, 'token')}
                        >
                          Скопировать токен
                        </Button>
                      </Stack>
                    )}
                    {resolvedLink && (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ sm: 'center' }}
                        useFlexGap
                      >
                        <Typography variant="body2" color="text.secondary">
                          Ссылка для пользователя:{' '}
                          <MuiLink href={resolvedLink} target="_blank" rel="noopener">
                            {resolvedLink}
                          </MuiLink>
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleCopyToClipboard(resolvedLink, 'link')}
                        >
                          Скопировать ссылку
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                )}
                {copyStatus && (
                  <Typography
                    variant="caption"
                    color={copyStatus.type === 'error' ? 'error' : 'success.main'}
                  >
                    {copyStatus.message}
                  </Typography>
                )}
              </Stack>
            );
          })()}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Paper elevation={0} className="surface-glow" sx={{ mb: 3, p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Текущая LLM-конфигурация</Typography>
              <Typography variant="body2" color="text.secondary">
                Выберите источник моделей и модель для извлечения требований, генерации и валидации сценариев.
              </Typography>

              {llmStatus && (
                <Alert severity={llmStatus.type} onClose={() => setLlmStatus(null)}>
                  {llmStatus.message}
                </Alert>
              )}

              {llmError && <Alert severity="error">{llmError}</Alert>}

              {llmLoading && !llmConfig ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={26} />
                </Box>
              ) : (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                    <FormControl
                      sx={{ minWidth: { md: 220 } }}
                      size="small"
                      disabled={llmSaving || llmLoading || tokenSaving}
                    >
                      <InputLabel id="llm-provider-select-label">Источник моделей</InputLabel>
                      <Select
                        labelId="llm-provider-select-label"
                        value={selectedProvider}
                        label="Источник моделей"
                        onChange={handleProviderChange}
                        renderValue={(provider) => (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, width: '100%' }}>
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {formatProviderLabel(provider)}
                            </Box>
                            {renderProviderStatusBadge(provider)}
                          </Box>
                        )}
                      >
                        {visibleProviders.map((provider) => (
                          <MenuItem key={provider} value={provider}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, width: '100%' }}>
                              <Box component="span">{formatProviderLabel(provider)}</Box>
                              {renderProviderStatusBadge(provider)}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={llmSaving || llmLoading || tokenSaving || !selectedProvider || visibleModels.length === 0}
                    >
                      <InputLabel id="llm-model-select-label">Модель</InputLabel>
                      <Select
                        labelId="llm-model-select-label"
                        value={selectedModel}
                        label="Модель"
                        onChange={(event) => setSelectedModel(event.target.value)}
                      >
                        {visibleModels.map((model) => (
                          <MenuItem key={model} value={model}>
                            {formatModelLabel(selectedProvider, model)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => loadLlmConfig(selectedProvider, false)}
                        disabled={llmSaving || llmLoading || tokenSaving || !selectedProvider}
                      >
                        Обновить список
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleApplyLlmModel}
                        disabled={
                          llmSaving
                          || llmLoading
                          || tokenSaving
                          || !selectedProvider
                          || !selectedModel
                          || (providerToApiProvider(selectedProvider) === providerToApiProvider(llmConfig?.currentProvider) && selectedModel === llmConfig?.currentModel)
                        }
                      >
                        {llmSaving ? 'Применение…' : 'Применить'}
                      </Button>
                    </Stack>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Сейчас выбрано: {formatProviderLabel(llmConfig?.currentProvider)} / {formatModelLabel(llmConfig?.currentProvider, llmConfig?.currentModel)}
                  </Typography>
                  {TOKEN_SUPPORTED_PROVIDERS.includes(selectedProvider) && (
                    <Stack spacing={1}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="password"
                          label={`Токен ${formatProviderLabel(selectedProvider)}`}
                          value={cloudTokenDraft}
                          onChange={(event) => setCloudTokenDraft(event.target.value)}
                          autoComplete="new-password"
                          disabled={tokenSaving || llmLoading}
                        />
                        <Button
                          variant="contained"
                          onClick={handleSaveCloudToken}
                          disabled={tokenSaving || llmLoading || !cloudTokenDraft.trim()}
                        >
                          {tokenSaving ? 'Сохранение…' : 'Сохранить токен'}
                        </Button>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Токен {formatProviderLabel(selectedProvider)}:{' '}
                        {(llmConfig?.providerAuthConfigured?.[selectedProvider] === true) ? 'настроен' : 'не задан'}
                      </Typography>
                    </Stack>
                  )}
                  {!llmLoading && visibleModels.length === 0 && !llmError && (
                    <Alert severity="warning">
                      Источник {formatProviderLabel(selectedProvider)} вернул пустой список моделей.
                    </Alert>
                  )}
                </Stack>
              )}
            </Stack>
          </Paper>

          <Paper elevation={0} className="surface-glow" sx={{ mb: 3, p: 3 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                <Typography variant="h6">Confluence</Typography>
                <Chip
                  size="small"
                  label={confluenceConfig?.connected ? 'Подключен' : 'Не настроен'}
                  color={confluenceConfig?.connected ? 'success' : 'default'}
                  variant={confluenceConfig?.connected ? 'filled' : 'outlined'}
                />
              </Stack>

              {confluenceStatus && (
                <Alert severity={confluenceStatus.type} onClose={() => setConfluenceStatus(null)}>
                  {confluenceStatus.message}
                </Alert>
              )}

              {confluenceLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={26} />
                </Box>
              ) : (
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Base URL"
                    value={confluenceForm.baseUrl}
                    onChange={handleConfluenceFieldChange('baseUrl')}
                    disabled={confluenceSaving}
                    placeholder="https://confluence.pflb.ru"
                  />
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      value={confluenceForm.email}
                      onChange={handleConfluenceFieldChange('email')}
                      disabled={confluenceSaving}
                    />
                    <FormControl size="small" sx={{ minWidth: { md: 180 } }} disabled={confluenceSaving}>
                      <InputLabel id="confluence-auth-type-label">Auth type</InputLabel>
                      <Select
                        labelId="confluence-auth-type-label"
                        label="Auth type"
                        value={confluenceForm.authType}
                        onChange={handleConfluenceFieldChange('authType')}
                      >
                        <MenuItem value="Bearer">Bearer</MenuItem>
                        <MenuItem value="Basic">Basic</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="password"
                      label="API token"
                      value={confluenceForm.apiToken}
                      onChange={handleConfluenceFieldChange('apiToken')}
                      autoComplete="new-password"
                      disabled={confluenceSaving}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSaveConfluenceConfig}
                      disabled={confluenceSaving || !confluenceForm.baseUrl.trim() || (!confluenceForm.apiToken.trim() && !confluenceConfig?.apiTokenConfigured)}
                    >
                      {confluenceSaving ? 'Сохранение…' : 'Сохранить'}
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    API token: {confluenceConfig?.apiTokenConfigured ? 'настроен' : 'не задан'}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Paper>

          <Paper elevation={0} className="surface-glow" sx={{ mb: 3, p: 3 }}>
            <Stack spacing={2} component="form" onSubmit={handleCreateNewUser}>
              <Typography variant="h6">Создать нового пользователя</Typography>
              <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                <TextField
                  fullWidth
                  label="Имя пользователя"
                  value={newUserForm.username}
                  onChange={handleNewUserFieldChange('username')}
                  error={newUserValidation.username}
                  helperText={newUserValidation.username ? 'Имя пользователя не может быть пустым' : undefined}
                  autoComplete="username"
                  disabled={creatingUser}
                />
              </Stack>
              <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }}>
                <TextField
                  label="Начальный лимит кредитов"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={newUserForm.credit}
                  onChange={handleNewUserFieldChange('credit')}
                  error={newUserValidation.credit}
                  helperText={newUserValidation.credit ? 'Введите неотрицательное число или оставьте поле пустым' : ' '}
                  sx={{ width: { xs: '100%', md: 240 } }}
                  disabled={creatingUser}
                />
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  type="submit"
                  variant="contained"
                  className="interactive-lift"
                  disabled={creatingUser}
                  sx={{ alignSelf: { xs: 'stretch', md: 'flex-start' } }}
                >
                  {creatingUser ? 'Создание…' : 'Создать пользователя'}
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                После создания пользователю будет выдан одноразовый токен для установки пароля. Передайте его адресату
                вместе со ссылкой.
              </Typography>
            </Stack>
          </Paper>

          <Divider sx={{ mb: 3 }} />

          <Paper elevation={0} className="surface-glow">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Имя пользователя</TableCell>
                  <TableCell>Роли</TableCell>
                <TableCell align="right">Остаток кредитов</TableCell>
                <TableCell align="right">Списано всего</TableCell>
                <TableCell align="center">Статус</TableCell>
                <TableCell align="center">Установить лимит</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((item) => {
                const roles = Array.isArray(item?.roles) ? item.roles : [];
                const key = getUserKey(item);
                const currentValue = creditInputs[key] ?? '';
                const isSaving = Boolean(saving[key]);
                return (
                  <TableRow key={item.id ?? item.username} hover>
                    <TableCell>{item.username}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {roles.length === 0 && <Chip size="small" label="нет" variant="outlined" />}
                        {roles.map((role) => (
                          <Chip key={role} size="small" label={role} color={role === 'ADMIN' ? 'primary' : 'default'} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{formatCreditDisplay(item.creditBalance)}</TableCell>
                    <TableCell align="right">{formatCreditDisplay(item.creditSpentTotal)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          label={item.enabled ? 'Активен' : 'Не активен'}
                          color={item.enabled ? 'success' : 'default'}
                          variant={item.enabled ? 'outlined' : 'filled'}
                        />
                        {item.passwordSetupRequired && (
                          <Chip
                            size="small"
                            label="Ожидает пароль"
                            color="warning"
                            variant="filled"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                          value={currentValue}
                          onChange={(event) => handleCreditChange(key, event.target.value)}
                          sx={{ width: 120 }}
                          disabled={isSaving}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          className="interactive-lift"
                          onClick={() => handleSetCredits(item)}
                          disabled={isSaving}
                        >
                          Сохранить
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    Нет данных для отображения
                  </TableCell>
                </TableRow>
              )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
}
